/**
 * useExternalLoadDrills – hook to fetch drill-level external load data.
 *
 * Uses Simulation dates (last 30 days) as the primary date source.
 * Falls back to PredictiveProtocolPlayer drill-level data when available,
 * otherwise extracts metrics from Simulation.session_acceleration_sprint.
 *
 * Exposes:
 *   dates        – array of ISO date strings with data (last 30 days)
 *   drillData    – { players: { [playerId]: { drills, totals, ... } }, source }
 *   loadingDates / loadingDrills
 *   selectedDate / setSelectedDate
 *   selectedDrill / setSelectedDrill   ("__global__" = summed totals)
 *   drillNames   – unique drill names for the current date
 *   dataSource   – "ppp" | "simulation" | null
 *   LATEST_DATE  – special value meaning "latest simulation per player"
 */
import { useState, useEffect, useMemo } from "react";
import { fetchAuth } from "../common/api.jsx";

export const GLOBAL_DRILL = "__global__";
export const LATEST_DATE = "__latest__";
export const ALL_PERIODS = "__all__";

const DEFAULT_DRILL_NAME = "global";

function getDrillNamesForPeriod(data, period) {
  if (!data?.players || !period) return [];
  const names = new Set();
  Object.values(data.players).forEach((p) => {
    p.drills?.forEach((d) => {
      if (d.period === period && d.name) names.add(d.name);
    });
  });
  return Array.from(names).sort();
}

function pickDefaultDrill(data, period) {
  const names = getDrillNamesForPeriod(data, period);
  return names.find((name) => String(name).toLowerCase() === DEFAULT_DRILL_NAME) || names[0] || GLOBAL_DRILL;
}

export function useExternalLoadDrills(season) {
  const [dates, setDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(true);

  const [selectedDate, setSelectedDate] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [loadingDrills, setLoadingDrills] = useState(false);

  const [selectedDrill, setSelectedDrill] = useState(GLOBAL_DRILL);
  const [selectedPeriod, setSelectedPeriod] = useState("AM");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingDates(true);
      try {
        const res = await fetchAuth(`/external_load/dates`);
        if (!res.ok) throw new Error("fetch dates failed");
        const data = await res.json();
        if (!cancelled) {
          setDates(data.dates ?? []);
        }
      } catch (e) {
       
      } finally {
        if (!cancelled) setLoadingDates(false);
      }
    })();
    return () => { cancelled = true; };
  }, [season]);

  // Auto-select most recent date once dates are loaded
  useEffect(() => {
    if (dates.length > 0 && selectedDate == null) {
      setSelectedDate(dates[0]);
    }
  }, [dates]);

  useEffect(() => {
    if (!selectedDate) {
      setDrillData(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingDrills(true);
      try {
        const res = await fetchAuth(`/external_load/drills?date=${selectedDate}`);
        if (!res.ok) throw new Error("fetch drills failed");
        const data = await res.json();
        if (!cancelled) {
          setDrillData(data);
          // Default to AM if available, else PM
          const periods = data.available_periods ?? [];
          const nextPeriod = periods.includes("AM") ? "AM" : "PM";
          setSelectedPeriod(nextPeriod);
          setSelectedDrill(pickDefaultDrill(data, nextPeriod));
        }
      } catch (e) {
        
      } finally {
        if (!cancelled) setLoadingDrills(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedDate]);

  // Available periods for the current date (from backend)
  const availablePeriods = useMemo(() => {
    return drillData?.available_periods ?? [];
  }, [drillData]);

  const drillNames = useMemo(() => {
    return getDrillNamesForPeriod(drillData, selectedPeriod);
  }, [drillData, selectedPeriod]);

  useEffect(() => {
    if (!drillData?.players || drillNames.length === 0) return;
    if (selectedDrill !== GLOBAL_DRILL && drillNames.includes(selectedDrill)) return;

    setSelectedDrill(
      drillNames.find((name) => String(name).toLowerCase() === DEFAULT_DRILL_NAME) || drillNames[0]
    );
  }, [drillData, drillNames, selectedDrill]);

  const playerMetrics = useMemo(() => {
    if (!drillData?.players) return {};
    const aggKeys = [
      "distance", "duration", "hsr", "msr", "spr",
      "sprint80_number", "acc_number", "dec_number", "cod_number",
    ];
    const maxKeys = ["sprint_max", "acc_max", "dec_max"];
    const out = {};
    for (const [pid, pdata] of Object.entries(drillData.players)) {
      // Filter drills by period
      const periodDrills = (pdata.drills ?? []).filter((d) => d.period === selectedPeriod);

      if (periodDrills.length === 0) {
        // Player has no drills in this period – skip completely
        continue;
      }

      if (selectedDrill === GLOBAL_DRILL) {
        // Recompute totals from period-filtered drills
        const totals = {};
        for (const k of aggKeys) {
          const vals = periodDrills.map((d) => d[k]).filter((v) => v != null);
          totals[k] = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) * 100) / 100 : null;
        }
        for (const k of maxKeys) {
          const vals = periodDrills.map((d) => d[k]).filter((v) => v != null);
          totals[k] = vals.length ? Math.round(Math.max(...vals) * 100) / 100 : null;
        }
        out[pid] = { ...pdata, metrics: totals };
      } else {
        const matched = periodDrills.filter((d) => d.name === selectedDrill);
        if (matched.length === 0) {
          out[pid] = { ...pdata, metrics: {} };
        } else {
          const agg = {};
          for (const k of aggKeys) {
            const vals = matched.map((d) => d[k]).filter((v) => v != null);
            agg[k] = vals.length ? vals.reduce((a, b) => a + b, 0) : null;
          }
          for (const k of maxKeys) {
            const vals = matched.map((d) => d[k]).filter((v) => v != null);
            agg[k] = vals.length ? Math.max(...vals) : null;
          }
          out[pid] = { ...pdata, metrics: agg };
        }
      }
    }
    return out;
  }, [drillData, selectedDrill, selectedPeriod]);

  const dataSource = drillData?.source ?? null; // "ppp" | "simulation" | null

  return {
    dates,
    loadingDates,
    selectedDate,
    setSelectedDate,
    drillData,
    loadingDrills,
    selectedDrill,
    setSelectedDrill,
    drillNames,
    playerMetrics,
    dataSource,
    availablePeriods,
    selectedPeriod,
    setSelectedPeriod,
    GLOBAL_DRILL,
  };
}
